import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { track } from "../services/analytics";
import { Menu, X, ChevronDown, Settings, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/DropdownMenu";
import Logo from "./Logo";

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const appNavLinks = [
    { path: "/meals", label: "Meals" },
    { path: "/meal-plans", label: "Meal Plans" },
    { path: "/meal-plan-templates", label: "Templates" },
    { path: "/shopping-lists", label: "Shopping Lists" },
  ];

  const publicNavLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/public-collections", label: "Collections" },
    { path: "/contact", label: "Contact" },
    ...(user ? [] : [{ path: "/pricing", label: "Pricing" }]),
  ];

  const handlePricingClick = () => {
    track("pricing_link_clicked", { location: "header" });
  };

  const mobileMenuHeightClass = mobileMenuOpen
    ? "max-h-[36rem] opacity-100"
    : "max-h-0 opacity-0";

  return (
    <header className="bg-primary-700 text-white shadow-lg sticky top-0 z-50 print:hidden">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and authenticated app navigation on the left */}
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className="flex items-center gap-2 text-xl font-bold hover:text-white transition-colors"
              aria-label="SevenServings home"
              title="Home"
            >
              <Logo className="h-8 w-8 text-white" />
              <span className="hidden xl:inline">SevenServings</span>
            </Link>

            {user && (
              <div
                className="hidden lg:flex items-center gap-1"
                aria-label="App navigation"
              >
                {appNavLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? "bg-primary-600 text-white"
                        : "text-white hover:bg-primary-600 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Public/utility links, auth controls, and user menu on the right */}
          <div className="flex items-center gap-4">
            {/* Desktop public navigation */}
            <div
              className="hidden lg:flex items-center gap-1"
              aria-label="Public navigation"
            >
              {publicNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={
                    link.path === "/pricing" ? handlePricingClick : undefined
                  }
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-primary-600 text-white"
                      : "text-white hover:bg-primary-600 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {user ? (
              <>
                {/* Desktop welcome text and user menu */}
                <div className="hidden lg:flex items-center gap-4">
                  <span className="text-sm text-white">
                    Welcome, <span className="font-medium">{user.email}</span>
                  </span>
                  <DropdownMenu
                    open={userDropdownOpen}
                    onOpenChange={setUserDropdownOpen}
                  >
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium
                                   bg-primary-600 hover:bg-primary-500 transition-colors"
                        aria-label={`Open user menu for ${user.email}`}
                      >
                        <div
                          className="h-8 w-8 rounded-full bg-primary-500 flex items-center
                                      justify-center text-white font-semibold"
                        >
                          {user.email.charAt(0).toUpperCase()}
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-2 border-b border-secondary-200">
                        <p className="text-sm font-medium text-secondary-900">
                          {user.email}
                        </p>
                      </div>
                      <DropdownMenuItem asChild>
                        <Link
                          to="/settings"
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Settings className="h-4 w-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={logout}
                        className="flex items-center gap-2 cursor-pointer text-error-600"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            ) : (
              /* Desktop auth links */
              <div className="hidden lg:flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-white
                           hover:bg-primary-600 rounded-md transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 text-sm font-medium bg-white text-primary-700
                           hover:bg-primary-50 rounded-md transition-colors"
                >
                  Register
                </Link>
              </div>
            )}

            {/* Mobile menu button always renders */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-white hover:bg-primary-600
                       transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu with smooth height animation */}
        <div
          id="mobile-menu"
          aria-label="Mobile menu"
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuHeightClass}`}
        >
          <div className="border-t border-primary-600 py-4">
            {user && (
              <div className="flex items-center gap-3 px-4 pb-4 border-b border-primary-600 mb-4">
                <div
                  className="h-10 w-10 rounded-full bg-primary-500 flex items-center
                              justify-center text-white font-semibold"
                >
                  {user.email.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium break-all">
                  {user.email}
                </span>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {publicNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (link.path === "/pricing") {
                      handlePricingClick();
                    }
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-primary-600 text-white"
                      : "text-white hover:bg-primary-600 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <>
                  <div className="border-t border-primary-600 mt-2 pt-2" />
                  {appNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive(link.path)
                          ? "bg-primary-600 text-white"
                          : "text-white hover:bg-primary-600 hover:text-white"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-primary-600 mt-2 pt-4">
                    <Link
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium
                               text-white hover:bg-primary-600 rounded-md transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium
                               text-white hover:bg-primary-600 rounded-md transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                </>
              )}
              {!user && (
                <div className="border-t border-primary-600 mt-2 pt-4 flex flex-col gap-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-center text-white
                             hover:bg-primary-600 rounded-md transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-center bg-white text-primary-700
                             hover:bg-primary-50 rounded-md transition-colors"
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
